import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

function errorText(error) {
  return error?.problem?.detail || error?.problem?.title || error?.message || "Request failed";
}

export function AdminWorkloadsTab() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState({ code: "", label: "", category: "", codeTouched: false });
  const [editing, setEditing] = useState({});
  const [busy, setBusy] = useState(null);

  async function reload() {
    try {
      const result = await api("/admin/skills");
      setItems(result.data || []);
    } catch (error) {
      toast.error(errorText(error));
    }
  }

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => [item.code, item.label, item.category]
      .some((value) => String(value || "").toLowerCase().includes(needle)));
  }, [items, query]);

  async function create() {
    const code = draft.code.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    const label = draft.label.trim();
    if (!code || !label) {
      toast.error("Enter both a workload name and code.");
      return;
    }
    setBusy("new");
    try {
      await api("/admin/skills", {
        method: "POST",
        body: { code, label, category: draft.category.trim() || null },
      });
      setDraft({ code: "", label: "", category: "", codeTouched: false });
      await reload();
      toast.success("Workload added to new projects.");
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setBusy(null);
    }
  }

  async function save(item) {
    const values = editing[item.id] || {};
    setBusy(item.id);
    try {
      await api(`/admin/skills/${item.id}`, {
        method: "PATCH",
        body: {
          label: (values.label ?? item.label).trim(),
          category: (values.category ?? item.category ?? "").trim() || null,
        },
      });
      setEditing((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      await reload();
      toast.success("Workload updated.");
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setBusy(null);
    }
  }

  async function setActive(item, active) {
    const usage = `${item.project_count} project${item.project_count === 1 ? "" : "s"} and ${item.profile_count} contractor profile${item.profile_count === 1 ? "" : "s"}`;
    if (!active && !window.confirm(`Remove “${item.label}” from future projects? It is already referenced by ${usage}. Historical records will be preserved.`)) return;
    setBusy(item.id);
    try {
      if (active) {
        await api(`/admin/skills/${item.id}`, { method: "PATCH", body: { active: true } });
      } else {
        await api(`/admin/skills/${item.id}`, { method: "DELETE" });
      }
      await reload();
      toast.success(active ? "Workload restored." : "Workload removed from future projects; history preserved.");
    } catch (error) {
      toast.error(errorText(error));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-5" data-testid="admin-workloads">
      <section className="avcp-card p-6">
        <h2 className="text-xl font-semibold">Required workloads</h2>
        <p className="mt-1 text-sm text-[var(--avcp-ink-soft)]">
          Control the workload choices clients can add to new projects. Removing a workload hides it from future selections without erasing project or contractor history.
        </p>
        <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_1fr_1fr_auto]">
          <label className="grid gap-1 text-sm">
            <span>Workload name</span>
            <input className="avcp-input" value={draft.label} placeholder="Example: System commissioning"
              onChange={(event) => {
                const label = event.target.value;
                setDraft((current) => ({
                  ...current,
                  label,
                  code: current.codeTouched
                    ? current.code
                    : label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
                }));
              }} />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Code</span>
            <input className="avcp-input font-mono" value={draft.code} placeholder="system_commissioning"
              onChange={(event) => setDraft({ ...draft, code: event.target.value, codeTouched: true })} />
          </label>
          <label className="grid gap-1 text-sm">
            <span>Category (optional)</span>
            <input className="avcp-input" value={draft.category} placeholder="Commissioning"
              onChange={(event) => setDraft({ ...draft, category: event.target.value })} />
          </label>
          <button className="avcp-btn primary self-end" disabled={busy === "new"} onClick={create}>
            {busy === "new" ? "Adding…" : "Add workload"}
          </button>
        </div>
      </section>

      <section className="avcp-card p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold">Workload catalog</h3>
            <p className="text-xs text-[var(--avcp-ink-soft)]">{items.filter((item) => item.active).length} active · {items.filter((item) => !item.active).length} inactive</p>
          </div>
          <input className="avcp-input max-w-sm" value={query} placeholder="Search workloads"
            onChange={(event) => setQuery(event.target.value)} />
        </div>
        <div className="overflow-x-auto">
          <table className="avcp-table min-w-[900px]">
            <thead><tr><th>Workload</th><th>Category</th><th>Status</th><th>Used by</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {visible.map((item) => {
                const values = editing[item.id] || {};
                const dirty = editing[item.id] != null;
                return (
                  <tr key={item.id} className={!item.active ? "opacity-70" : ""}>
                    <td>
                      <input className="avcp-input text-sm" value={values.label ?? item.label}
                        onChange={(event) => setEditing({ ...editing, [item.id]: { ...values, label: event.target.value } })} />
                      <div className="mt-1 font-mono text-xs text-[var(--avcp-ink-soft)]">{item.code}</div>
                    </td>
                    <td>
                      <input className="avcp-input text-sm" value={values.category ?? item.category ?? ""} placeholder="Uncategorized"
                        onChange={(event) => setEditing({ ...editing, [item.id]: { ...values, category: event.target.value } })} />
                    </td>
                    <td><span className={`avcp-pill ${item.active ? "ok" : "warn"}`}>{item.active ? "Active" : "Inactive"}</span></td>
                    <td className="text-sm">{item.project_count} projects · {item.profile_count} profiles</td>
                    <td>
                      <div className="flex flex-wrap justify-end gap-2">
                        <button className="avcp-btn ghost text-xs !px-3 !py-2" disabled={!dirty || busy === item.id} onClick={() => save(item)}>Save</button>
                        <button className={`avcp-btn text-xs !px-3 !py-2 ${item.active ? "danger" : "ghost"}`} disabled={busy === item.id}
                          onClick={() => setActive(item, !item.active)}>
                          {item.active ? "Remove from new projects" : "Restore"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visible.length === 0 && <tr><td colSpan="5" className="text-sm text-[var(--avcp-ink-soft)]">No workloads match.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
